import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { buildCallsModelError } from '@gem/call/Model/error/errorBuilder';
import {
  callUnauthorizedB2bWaitingRoomAccessStatusRequestMap,
  mapCallUnauthorizedInfo,
  mapCallUnauthorizedMembers,
} from '@gem/call/Model/unauthorized.call/callModel.unauthorized.mapper';
import {
  joinToCallRequest as unauthorizedJoinToCallRequest,
  finishCallForAllRequest as unauthorizedFinishCallForAllRequest,
  kickMemberRequest as unauthorizedKickMemberRequest,
  forceMuteRequest as unauthorizedForceMuteRequest,
  getCallMembersRequest as unauthorizedGetCallMembersRequest,
  getCallMembersByUidRequest as unauthorizedGetCallMembersByUidRequest,
  getCallMembersByIdsRequest as unauthorizedGetCallMembersByIdsRequest,
  getCallLinkRequest as unauthorizedGetCallLinkRequest,
  waitingRoomAccessRequest as unauthorizedWaitingRoomAccessRequest,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/requests/callRequests';
import {
  addMembersToCallRequest,
  finishCallForAllRequest,
  forceMuteRequest,
  joinToCallRequest,
  getCallMembersByUidRequest,
  getCallMembersRequest,
  getCallMembersByIdsRequest,
  kickMemberRequest,
  removeMembersFromCallRequest,
  startRecordingRequest,
  stopRecordingRequest,
  getCallLinkRequest,
  updateWaitingRoomForMemberRequest,
  updateWaitingRoomForAllRequest,
  waitingRoomAccessRequest,
  declineCallRequest,
} from '@gem/call/WsApi/wsCallsApi/requests/callRequests';
import { logger } from '@gem/modules/logger';

import {
  initCallRealtimeEvents,
  initWaitingRoomRealtimeEvents,
} from './b2b.call/callModel.b2b.events';
import {
  callB2bRecordingTypeResponseMap,
  callB2bWaitingRoomAccessStatusRequestMap,
  callB2bWaitingRoomAllStatusRequestMap,
  callB2bWaitingRoomMemberStatusRequestMap,
  mapCallB2bInfo,
  mapCallB2bMembers,
} from './b2b.call/callModel.b2b.mapper';
import {
  CallAction,
  CallProviderType,
  CallsServerErrorCode,
  FinishCallReason,
  IBooleanResponse,
  ICallInfo,
  ICallMember,
  IRequestWaitingRoomAccessParams,
  IUpdateWaitingRoomForAllParams,
  IUpdateWaitingRoomForMemberParams,
  RecordingType,
  TInitCallListenerFunc,
  TInitWaitingRoomCallListenerFunc,
} from './callModel.types';
import {
  initUnauthorizedCallRealtimeEvents,
  initUnauthorizedWaitingRoomRealtimeEvents,
} from './unauthorized.call/callModel.unauthorized.events';

export let isUnauthorizedView = false;

export const setIsUnauthorizedView = (value: boolean): void => {
  isUnauthorizedView = value;
};

export const getCallInfo = async (props: {
  channelId: string;
  providerType: CallProviderType;
}): Promise<ICallInfo> => {
  try {
    if (isUnauthorizedView) {
      const result = await unauthorizedJoinToCallRequest({
        channelId: props.channelId,
      });

      if (result.type.case === 'success') {
        const callInfo = result.type.value?.call;

        return mapCallUnauthorizedInfo(callInfo!);
      } else if (result.type.case === 'hasNoPermissionsForCall') {
        throw new Error(CallsServerErrorCode.HasNoPermissionsToCall);
      } else if (result.type.case === 'participantLimitReached') {
        throw new Error(CallsServerErrorCode.ParticipantLimitReached);
      }
      throw new Error("Unauthorized View doesn't have createCall request");
    }

    const result = await joinToCallRequest({
      channelId: props.channelId,
    });

    if (result.type.case === 'success') {
      const callInfo = result.type.value?.call;

      return mapCallB2bInfo(callInfo!);
    } else if (result.type.case === 'hasNoPermissionsForCall') {
      throw new Error(CallsServerErrorCode.HasNoPermissionsToCall);
    } else if (result.type.case === 'participantLimitReached') {
      throw new Error(CallsServerErrorCode.ParticipantLimitReached);
    } else {
      throw new Error('unexpected error');
    }
  } catch (error) {
    logger.get('API').error('calls.getCallInfo', error);
    throw buildCallsModelError(error);
  }
};

export const finishCall = async (
  channelId: string,
  reason: FinishCallReason
): Promise<IBooleanResponse> => {
  if (isUnauthorizedView) {
    if (reason === FinishCallReason.FinishAll) {
      const result = await unauthorizedFinishCallForAllRequest({
        channelId,
      });

      return { result: result.type.case === 'success' };
    }

    return { result: true };
  }

  if (reason === FinishCallReason.FinishAll) {
    const result = await finishCallForAllRequest({
      channelId,
    });

    return { result: result.type.case === 'success' };
  }

  if (reason === FinishCallReason.Declined) {
    const result = await declineCallRequest({
      channelId,
    });

    return { result: result.type.case === 'success' };
  }

  return { result: true };
};

export const startRecording = async (params: {
  channelId: string;
  title: string;
  recordingType: RecordingType;
}): Promise<{ recordingId: string }> => {
  try {
    if (isUnauthorizedView) {
      throw new Error("Unauthorized View doesn't have startRecording request");
    }

    const result = await startRecordingRequest({
      channelId: params.channelId,
      recordingType: callB2bRecordingTypeResponseMap[params.recordingType],
      title: params.title || undefined,
    });

    if (result.type.case === 'success') {
      return result.type.value;
    } else if (result.type.case === 'recordingIsStartedAlready') {
      throw new Error(CallsServerErrorCode.RecordingIsStartedAlready);
    } else if (result.type.case === 'channelClosed') {
      throw new Error(CallsServerErrorCode.ChannelClosed);
    } else {
      throw result.type.value;
    }
  } catch (e) {
    logger.get('API').error('calls.stopRecording', e);
    throw buildCallsModelError(e);
  }
};

export const stopRecording = async (params: {
  channelId: string;
  recordingId: string;
}): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      throw new Error("Unauthorized View doesn't have stopRecording request");
    }

    await stopRecordingRequest(params);
  } catch (e) {
    logger.get('API').error('calls.stopRecording', e);
    throw buildCallsModelError(e);
  }
};

export const setActionCall = async (params: {
  userId: string;
  action: CallAction;
  deviceId: ProviderUid;
  channelId: string;
}): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      if (params.action === CallAction.RaiseHand) {
        throw new Error("Unauthorized View doesn't have RaiseHand request");
      }
      if (params.action === CallAction.PutHand) {
        throw new Error("Unauthorized View doesn't have putHand request");
      }
      if (params.action === CallAction.ForceMute) {
        await unauthorizedForceMuteRequest({
          channelId: params.channelId,
          targetMemberId: BigInt(params.userId),
        });
        return;
      }
      if (params.action === CallAction.ForceFinish) {
        await unauthorizedKickMemberRequest({
          channelId: params.channelId,
          targetMemberId: BigInt(params.userId),
        });
        return;
      }
    }

    if (params.action === CallAction.ForceMute) {
      await forceMuteRequest({
        channelId: params.channelId,
        targetMemberId: BigInt(params.userId),
        targetDeviceId: String(params.deviceId),
      });
      return;
    }
    if (params.action === CallAction.ForceFinish) {
      await kickMemberRequest({
        channelId: params.channelId,
        targetMemberId: BigInt(params.userId),
        targetDeviceId: String(params.deviceId),
      });
      return;
    }
  } catch (error) {
    logger.get('API').error('calls.setActionCall', error);
  }
};

export const removeCallMember = async (params: {
  channelId: string;
  userId: string;
}): Promise<{ result: boolean }> => {
  logger.get('API').debug('calls.removeCallMember', params);
  try {
    if (isUnauthorizedView) {
      throw new Error("Unauthorized View doesn't have removeCallMember request");
    }

    const result = await removeMembersFromCallRequest({
      channelId: params.channelId,
      memberIds: [BigInt(params.userId)],
    });

    return { result: result.type.case === 'success' };
  } catch (e) {
    logger.get('API').error('calls.removeCallMember', e);
    throw buildCallsModelError(e);
  }
};

export const getUnionCallLink = async (body: { channelId: string }): Promise<string> => {
  try {
    if (isUnauthorizedView) {
      const result = await unauthorizedGetCallLinkRequest({
        channelId: body.channelId,
      });

      if (result.type.case === 'success') {
        return result.type.value?.callLink;
      } else {
        throw result.type.value;
      }
    }

    const result = await getCallLinkRequest({ channelId: body.channelId });
    if (result.type.case === 'success') {
      return result.type.value.callLink;
    } else {
      throw result.type.value;
    }
  } catch (e) {
    logger.get('API').error('calls.getUnionCallLink', e);
    throw buildCallsModelError(e);
  }
};

export const getCallMembers = async (props: { channelId: string }): Promise<ICallMember[]> => {
  try {
    if (isUnauthorizedView) {
      const result = await unauthorizedGetCallMembersRequest({
        channelId: props.channelId,
      });

      if (result.type.case === 'success') {
        const members = result.type.value?.members || [];

        return mapCallUnauthorizedMembers(members);
      } else {
        throw result.type.value;
      }
    }

    const result = await getCallMembersRequest({
      channelId: props.channelId,
    });

    if (result.type.case === 'success') {
      const members = result.type.value?.members || [];

      return mapCallB2bMembers(members);
    } else {
      throw result.type.value;
    }
  } catch (e) {
    logger.get('API').error('calls.getCallMembers', e);
    return [];
  }
};

export const getCallMembersByUid = async (props: {
  channelId: string;
  ids: ProviderUid[];
}): Promise<ICallMember[]> => {
  try {
    if (isUnauthorizedView) {
      const result = await unauthorizedGetCallMembersByUidRequest({
        channelId: props.channelId,
        ids: props.ids.map((id) => id.toString()),
      });

      if (result.type.case === 'success') {
        const members = result.type.value?.members || [];

        return mapCallUnauthorizedMembers(members);
      } else {
        throw result.type.value;
      }
    }
    const result = await getCallMembersByUidRequest({
      channelId: props.channelId,
      ids: props.ids.map((id) => id.toString()),
    });

    if (result.type.case === 'success') {
      const members = result.type.value?.members || [];

      return mapCallB2bMembers(members);
    } else {
      throw result.type.value;
    }
  } catch (e) {
    logger.get('API').error('calls.getCallMembersByUid', e);
    throw buildCallsModelError(e);
  }
};

export const getCallMembersByIds = async (props: {
  channelId: string;
  ids: string[];
}): Promise<ICallMember[]> => {
  try {
    if (isUnauthorizedView) {
      const result = await unauthorizedGetCallMembersByIdsRequest({
        channelId: props.channelId,
        ids: props.ids.map((id) => BigInt(id)),
      });

      if (result.type.case === 'success') {
        const members = result.type.value?.members || [];

        return mapCallUnauthorizedMembers(members);
      } else {
        throw result.type.value;
      }
    }
    const result = await getCallMembersByIdsRequest({
      channelId: props.channelId,
      ids: props.ids.map((id) => BigInt(id)),
    });

    if (result.type.case === 'success') {
      const members = result.type.value?.members || [];

      return mapCallB2bMembers(members);
    } else {
      throw result.type.value;
    }
  } catch (e) {
    logger.get('API').error('calls.getCallMembersByIds', e);
    throw buildCallsModelError(e);
  }
};

export const addCallMembers = async (channelId: string, userIds: string[]): Promise<void> => {
  await addMembersToCallRequest({
    channelId,
    memberIds: userIds.map((item) => BigInt(item)),
  });
};

export const initCallListener: TInitCallListenerFunc = (params) => {
  return isUnauthorizedView
    ? initUnauthorizedCallRealtimeEvents(params)
    : initCallRealtimeEvents(params);
};

export const initWaitingRoomCallListener: TInitWaitingRoomCallListenerFunc = (params) => {
  return isUnauthorizedView
    ? initUnauthorizedWaitingRoomRealtimeEvents(params)
    : initWaitingRoomRealtimeEvents(params);
};

export async function updateWaitingRoomForMember(
  params: IUpdateWaitingRoomForMemberParams
): Promise<IBooleanResponse> {
  try {
    const result = await updateWaitingRoomForMemberRequest({
      channelId: params.channelId,
      memberId: BigInt(params.memberId),
      status: callB2bWaitingRoomMemberStatusRequestMap[params.status],
    });

    if (result.type.case === 'success') {
      return { result: true };
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calls.updateWaitingRoomForMember', error);
    throw buildCallsModelError(error);
  }
}

export async function updateWaitingRoomForAll(
  params: IUpdateWaitingRoomForAllParams
): Promise<IBooleanResponse> {
  try {
    const result = await updateWaitingRoomForAllRequest({
      channelId: params.channelId,
      status: callB2bWaitingRoomAllStatusRequestMap[params.status],
    });

    if (result.type.case === 'success') {
      return { result: true };
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calls.updateWaitingRoomForAll', error);
    throw buildCallsModelError(error);
  }
}

export async function requestWaitingRoomAccess(
  params: IRequestWaitingRoomAccessParams
): Promise<IBooleanResponse> {
  try {
    let result;
    if (isUnauthorizedView) {
      result = await unauthorizedWaitingRoomAccessRequest({
        channelId: params.channelId,
        request: callUnauthorizedB2bWaitingRoomAccessStatusRequestMap[params.status],
      });
    } else {
      result = await waitingRoomAccessRequest({
        channelId: params.channelId,
        request: callB2bWaitingRoomAccessStatusRequestMap[params.status],
      });
    }

    if (result.type.case === 'success') {
      return { result: true };
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calls.requestWaitingRoomAccess', error);
    throw buildCallsModelError(error);
  }
}
